import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Coffee, Sun, Moon, Cookie, Send, Sparkles, FileText, Download, MessageCircle } from 'lucide-react';
import { authService } from '../../services/auth/authService';
import { DashboardHeader } from '../../components/layout/DashboardHeader/DashboardHeader';
import { DashboardSidebar } from '../../components/layout/DashboardSidebar/DashboardSidebar';
import { createSidebarItems } from '../../components/layout/DashboardSidebar/sidebarItems';
import {
  mealPlanService,
  MealPlanSummary,
  MealPlanVersionResponse,
  MealPlanVersionSummary,
  MealPlanTotalsSummary,
  MealPlanChatStatusResponse,
} from '../../services/nutrition/mealPlanService';
import { patientsService } from '../../services/patients/patientsService';
import { sendDietPlanViaWhatsApp } from '../../services/messaging/messagingService';
import { MealPlanOverviewCards } from './MealPlanOverviewCards';
import PdfViewerModal from '../../components/common/PdfViewerModal';
import AlertModal from '../../components/common/AlertModal';
import './PatientDetailPage.css';

const defaultMealTiming = {
  breakfast: '08:00',
  lunch: '13:00',
  dinner: '20:00',
  snacks: '15:00',
};

/** Format meal_key from API (e.g. "early morning", "morning drink") for display. */
function formatMealKeyForDisplay(mealKey: string): string {
  if (!mealKey || typeof mealKey !== 'string') return 'Meal';
  return mealKey.trim().split(/\s+/).map((s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()).join(' ');
}

const NUTRIENT_ORDER = [
  'calories', 'protein_g', 'carbs_g', 'fat_g', 'fiber_g', 'sugar_g', 'sodium_mg',
  'vitamin_a_mcg', 'vitamin_c_mg', 'vitamin_d_mcg', 'vitamin_e_mg', 'vitamin_k_mcg',
  'vitamin_b1_mg', 'vitamin_b2_mg', 'vitamin_b3_mg', 'vitamin_b6_mg', 'vitamin_b12_mcg', 'folate_mcg',
  'calcium_mg', 'iron_mg', 'magnesium_mg', 'phosphorus_mg', 'potassium_mg', 'zinc_mg',
  'copper_mg', 'manganese_mg', 'selenium_mcg',
];

type VersionViewTab = 'overview' | 'nutrient_comparison' | 'daily_meal_plan' | 'chat';
type PrimaryTab = 'settings' | 'details';

const MealPlanVersionViewPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { patientId, planId } = useParams<{ patientId: string; planId: string }>();
  const initialVersionIdFromState = (location.state as { versionId?: string } | null)?.versionId;
  const [plan, setPlan] = useState<MealPlanSummary | null>(null);
  const [planVersion, setPlanVersion] = useState<MealPlanVersionResponse | null>(null);
  const [versions, setVersions] = useState<MealPlanVersionSummary[]>([]);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [totalsSummary, setTotalsSummary] = useState<MealPlanTotalsSummary | null>(null);
  const [chatStatus, setChatStatus] = useState<MealPlanChatStatusResponse | null>(null);
  const [primaryTab, setPrimaryTab] = useState<PrimaryTab>('details');
  const [viewTab, setViewTab] = useState<VersionViewTab>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [patientName, setPatientName] = useState<string | null>(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [expandedDay, setExpandedDay] = useState<number | null>(0);
  const [detailTab, setDetailTab] = useState<Record<string, 'ingredients' | 'preparations'>>({});
  const [expandedGrocery, setExpandedGrocery] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [sendingChat, setSendingChat] = useState(false);
  const [loadingChatStatus, setLoadingChatStatus] = useState(false);
  const [pdfLoading, setPdfLoading] = useState<'view' | 'download' | null>(null);
  const [pdfViewerUrl, setPdfViewerUrl] = useState<string | null>(null);
  const [whatsappSending, setWhatsappSending] = useState(false);
  const [whatsappMessage, setWhatsappMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const pdfViewerUrlRef = useRef<string | null>(null);
  const chatPollRef = useRef<NodeJS.Timeout | null>(null);
  const chatMessagesContainerRef = useRef<HTMLDivElement | null>(null);

  const loadPlan = useCallback(async () => {
    if (!patientId || !planId) {
      setError('Invalid patient or plan');
      setLoading(false);
      return;
    }
    const numericPatientId = Number(patientId);
    if (Number.isNaN(numericPatientId)) {
      setError('Invalid patient');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const plans = await mealPlanService.listPlansForPatient(numericPatientId);
      const found = plans.find((p) => p.plan_id === planId);
      if (!found) {
        setError('Plan not found');
        setLoading(false);
        return;
      }
      setPlan(found);
      try {
        const p = await patientsService.getPatients();
        const patient = p.find((x) => x.id === numericPatientId);
        setPatientName(patient?.name ?? null);
      } catch {
        setPatientName(null);
      }
      const [currentVersion, versionList] = await Promise.all([
        mealPlanService.getCurrentVersion(planId),
        mealPlanService.listVersions(planId).catch(() => []),
      ]);
      const sortedVersions = (versionList || []).slice().sort((a, b) => (b.version ?? 0) - (a.version ?? 0));
      setVersions(sortedVersions);
      const versionToSelect = initialVersionIdFromState && sortedVersions.some((v) => v.version_id === initialVersionIdFromState)
        ? initialVersionIdFromState
        : currentVersion.version_id;
      setSelectedVersionId(versionToSelect);
      if (versionToSelect === currentVersion.version_id) {
        setPlanVersion(currentVersion);
        try {
          const summary = await mealPlanService.getTotalsSummary(planId, currentVersion.version_id, undefined);
          setTotalsSummary(summary);
        } catch {
          setTotalsSummary(null);
        }
      } else {
        try {
          const ver = await mealPlanService.getVersion(planId, versionToSelect);
          setPlanVersion(ver);
          const summary = await mealPlanService.getTotalsSummary(planId, versionToSelect, undefined);
          setTotalsSummary(summary);
        } catch {
          setPlanVersion(null);
          setTotalsSummary(null);
        }
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load plan');
    } finally {
      setLoading(false);
    }
  }, [patientId, planId, initialVersionIdFromState]);

  useEffect(() => {
    if (!planId || !selectedVersionId || selectedVersionId === planVersion?.version_id) return;
    let cancelled = false;
    (async () => {
      try {
        const ver = await mealPlanService.getVersion(planId, selectedVersionId);
        if (!cancelled) setPlanVersion(ver);
        const summary = await mealPlanService.getTotalsSummary(planId, selectedVersionId, undefined);
        if (!cancelled) setTotalsSummary(summary);
      } catch (e) {
        if (!cancelled) setPlanVersion(null);
        if (!cancelled) setTotalsSummary(null);
      }
    })();
    return () => { cancelled = true; };
  }, [planId, selectedVersionId]);

  const fetchChatStatus = useCallback(async () => {
    if (!planId) return null;
    try {
      setLoadingChatStatus(true);
      const status = await mealPlanService.getChatStatus(planId, chatStatus?.session_id ?? undefined, selectedVersionId ?? undefined);
      const normalized = typeof (status as any)?.plan_preview === 'string'
        ? { ...status, plan_preview: (() => {
            try { return JSON.parse((status as any).plan_preview); } catch { return (status as any).plan_preview; }
          })() }
        : status;
      setChatStatus(normalized);
      return normalized;
    } catch (e) {
      console.warn('Chat status failed:', e);
      return null;
    } finally {
      setLoadingChatStatus(false);
    }
  }, [planId, selectedVersionId]);

  const isChatWorkflowInProgress = useCallback((status?: MealPlanChatStatusResponse | null) => {
    if (!status) return false;
    if (status.plan_approved || status.status === 'approved' || status.status === 'cancelled') return false;
    if (status.status === 'ready_for_review' || status.status === 'waiting_for_modifications') return false;
    return status.status === 'processing' || status.workflow_task_status === 'processing';
  }, []);

  const isTabActive = () => document.visibilityState === 'visible' && document.hasFocus();

  useEffect(() => {
    loadPlan();
  }, [loadPlan]);

  useEffect(() => {
    if (!planId || viewTab !== 'chat') return;

    let cancelled = false;
    let interval: NodeJS.Timeout | null = null;

    const stopPolling = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
      if (chatPollRef.current) {
        clearInterval(chatPollRef.current);
        chatPollRef.current = null;
      }
    };

    const startPolling = () => {
      if (interval) return;
      interval = setInterval(async () => {
        if (!isTabActive()) return;
        const latest = await fetchChatStatus();
        if (!isChatWorkflowInProgress(latest)) {
          stopPolling();
        }
      }, 8000);
      chatPollRef.current = interval;
    };

    const bootstrap = async () => {
      const latest = await fetchChatStatus();
      if (cancelled) return;
      if (isTabActive() && isChatWorkflowInProgress(latest)) {
        startPolling();
      } else {
        stopPolling();
      }
    };

    const handleTabActivity = async () => {
      if (cancelled) return;
      if (!isTabActive()) {
        stopPolling();
        return;
      }
      const latest = await fetchChatStatus();
      if (isChatWorkflowInProgress(latest)) {
        startPolling();
      } else {
        stopPolling();
      }
    };

    void bootstrap();
    document.addEventListener('visibilitychange', handleTabActivity);
    window.addEventListener('focus', handleTabActivity);
    window.addEventListener('blur', stopPolling);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleTabActivity);
      window.removeEventListener('focus', handleTabActivity);
      window.removeEventListener('blur', stopPolling);
      stopPolling();
    };
  }, [planId, viewTab, fetchChatStatus, isChatWorkflowInProgress]);

  const sendChatMessage = useCallback(async (message: string, sessionId?: number | null) => {
    if (!planId || !message.trim() || sendingChat) return;
    try {
      setSendingChat(true);
      await mealPlanService.sendChatMessage(
        planId,
        { content: message.trim(), session_id: sessionId ?? undefined, version_id: selectedVersionId ?? undefined },
        { idempotencyKey: typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}` },
      );
      setChatInput('');
      await fetchChatStatus();
    } catch (e: any) {
      console.error('Send chat failed:', e);
      setError(e?.response?.data?.detail || e?.message || 'Failed to send');
    } finally {
      setSendingChat(false);
    }
  }, [planId, selectedVersionId, fetchChatStatus, sendingChat]);

  const handleBack = () => {
    if (plan?.goal_id != null) {
      navigate(`/patients/${patientId}/diet-plan/${plan.goal_id}/daily-meals`);
    } else {
      navigate(`/patients/${patientId}?tab=mealPlan&showMealPlans=true`);
    }
  };

  const handleViewPdf = async () => {
    if (!planId || !selectedVersionId || pdfLoading) return;
    setPdfLoading('view');
    try {
      const { blob } = await mealPlanService.getWeeklySchedulePdf(planId, selectedVersionId, 'inline', true);
      const url = URL.createObjectURL(blob);
      pdfViewerUrlRef.current = url;
      setPdfViewerUrl(url);
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to load PDF';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setPdfLoading(null);
    }
  };

  const handleClosePdfViewer = () => {
    const url = pdfViewerUrlRef.current;
    if (url) {
      URL.revokeObjectURL(url);
      pdfViewerUrlRef.current = null;
    }
    setPdfViewerUrl(null);
  };

  const handleDownloadPdf = async () => {
    if (!planId || !selectedVersionId || pdfLoading) return;
    setPdfLoading('download');
    try {
      const { blob, filename } = await mealPlanService.getWeeklySchedulePdf(planId, selectedVersionId, 'attachment');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to download PDF';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setPdfLoading(null);
    }
  };

  const handleSendViaWhatsApp = async () => {
    if (!planId || !selectedVersionId || whatsappSending) return;
    setWhatsappMessage(null);
    setWhatsappSending(true);
    try {
      const res = await sendDietPlanViaWhatsApp(planId, selectedVersionId);
      const status = res?.status ?? 'sent';
      setWhatsappMessage({
        type: 'success',
        text: `Diet plan sent to WhatsApp (status: ${status}). Check Recent message logs for delivery status; failed sends (e.g. outside 24h window) show as Undelivered there.`,
      });
    } catch (e: any) {
      const msg = e?.response?.data?.detail ?? e?.message ?? 'Failed to send via WhatsApp';
      setWhatsappMessage({ type: 'error', text: typeof msg === 'string' ? msg : JSON.stringify(msg) });
    } finally {
      setWhatsappSending(false);
    }
  };

  const scrollChatToBottom = () => {
    const el = chatMessagesContainerRef.current;
    if (el) try { el.scrollTop = el.scrollHeight; } catch { /* noop */ }
  };

  useEffect(() => {
    if (chatStatus?.conversation_log?.length) {
      const t = setTimeout(scrollChatToBottom, 80);
      return () => clearTimeout(t);
    }
  }, [chatStatus?.conversation_log]);

  if (!authService.isAuthenticated()) return null;

  const sidebarItems = createSidebarItems(navigate, window.location.pathname);

  if (loading && !plan) {
    return (
      <div className="dashboard">
        <DashboardHeader onProfileClick={() => navigate('/profile')} onLogout={() => navigate('/login')} />
        <div className="dashboard-layout">
          <DashboardSidebar items={sidebarItems} />
          <div className="dashboard-container">
            <main className="dashboard-main-content">
              <div className="section-card"><div style={{ padding: 40, textAlign: 'center' }}>Loading...</div></div>
            </main>
          </div>
        </div>
      </div>
    );
  }

  if (error && !plan) {
    return (
      <div className="dashboard">
        <DashboardHeader onProfileClick={() => navigate('/profile')} onLogout={() => navigate('/login')} />
        <div className="dashboard-layout">
          <DashboardSidebar items={sidebarItems} />
          <div className="dashboard-container">
            <main className="dashboard-main-content">
              <div className="section-card">
                <div style={{ padding: 40, textAlign: 'center', color: '#ef4444' }}>
                  <p>{error}</p>
                  <button onClick={handleBack} className="primary-button" style={{ marginTop: 16 }}>Go Back</button>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    );
  }

  if (!plan) return null;

  const sessionId = chatStatus?.session_id ?? null;
  const hasSession = sessionId != null;

  return (
    <div className="dashboard">
      <DashboardHeader onProfileClick={() => navigate('/profile')} onLogout={() => navigate('/login')} />
      <div className="dashboard-layout">
        <DashboardSidebar items={sidebarItems} />
        <div className="dashboard-container">
          <main className="dashboard-main-content">
            <div className="section-card">
              <nav className="detail-breadcrumb" aria-label="Breadcrumb">
                <Link to="/patients" className="breadcrumb-link">Patients</Link>
                <span className="breadcrumb-sep">›</span>
                {patientId && (
                  <>
                    <Link to={`/patients/${patientId}`} className="breadcrumb-link">{patientName ?? 'Patient'}</Link>
                    <span className="breadcrumb-sep">›</span>
                    <Link to={`/patients/${patientId}/diet-plans`} className="breadcrumb-link">Diet Plans</Link>
                    <span className="breadcrumb-sep">›</span>
                    {plan.goal_id != null ? (
                      <Link to={`/patients/${patientId}/diet-plan/${plan.goal_id}/daily-meals`} className="breadcrumb-link">Meal Plans</Link>
                    ) : (
                      <Link to={`/patients/${patientId}/diet-plans`} className="breadcrumb-link">Meal Plans</Link>
                    )}
                    <span className="breadcrumb-sep">›</span>
                  </>
                )}
                <span className="breadcrumb-current">View Plan</span>
              </nav>

              <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                <h2 className="section-title" style={{ margin: 0 }}>Meal Plans</h2>
                <button type="button" onClick={handleBack} className="secondary-button" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <ChevronLeft size={16} /> Back to list
                </button>
              </div>

              {/* Diet plan summary */}
              <div className="np-subcard" style={{ marginBottom: 20, padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                <div>
                  <div className="plan-info-label" style={{ fontSize: '0.7rem', letterSpacing: '0.05em', color: '#6b7280', marginBottom: 4 }}>DIET PLAN</div>
                  <div style={{ fontSize: '1rem', fontWeight: 600, color: '#111827' }}>{plan.name || 'Daily meal plan'}</div>
                </div>
                <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                  Effective Since {plan.start_date ? new Date(plan.start_date).toLocaleDateString() : '—'}
                </div>
              </div>

              {/* Primary tabs: Settings | Daily Meal Plan */}
              <div className="patient-tabs" style={{ marginBottom: 16 }}>
                <button
                  type="button"
                  className={`patient-tab ${primaryTab === 'settings' ? 'active' : ''}`}
                  onClick={() => setPrimaryTab('settings')}
                >
                  Settings
                </button>
                <button
                  type="button"
                  className={`patient-tab ${primaryTab === 'details' ? 'active' : ''}`}
                  onClick={() => setPrimaryTab('details')}
                >
                  Daily Meal Plan
                </button>
              </div>

              {primaryTab === 'settings' && (
                <div className="np-subcard" style={{ marginBottom: 16, padding: 16 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
                    <div><div className="plan-info-label">Name</div><div className="plan-info-value">{plan.name || '—'}</div></div>
                    <div><div className="plan-info-label">Mode / Variant</div><div className="plan-info-value">{plan.mode || '—'} / {plan.variant || '—'}</div></div>
                    <div><div className="plan-info-label">Status</div><div className="plan-info-value">{plan.status || '—'}</div></div>
                    <div><div className="plan-info-label">Cuisine</div><div className="plan-info-value">{plan.cuisine_pattern || '—'}</div></div>
                    <div><div className="plan-info-label">Start date</div><div className="plan-info-value">{plan.start_date ? new Date(plan.start_date).toLocaleDateString() : '—'}</div></div>
                    <div><div className="plan-info-label">End date</div><div className="plan-info-value">{plan.end_date ? new Date(plan.end_date).toLocaleDateString() : '—'}</div></div>
                  </div>
                </div>
              )}

              {primaryTab === 'details' && (
                <>
                  {/* Version dropdown: version number and date */}
                  {planVersion && (
                    <div className="np-subcard" style={{ marginBottom: 16, padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                      <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                        Selected version: <code style={{ background: '#f3f4f6', padding: '2px 6px', borderRadius: 4 }}>{planVersion.version_id}</code>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <label htmlFor="version-select-view" style={{ fontSize: '0.85rem', color: '#6b7280' }}>Version</label>
                        <select
                          id="version-select-view"
                          value={selectedVersionId ?? ''}
                          onChange={(e) => setSelectedVersionId(e.target.value || null)}
                          style={{ minWidth: 220, padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', fontSize: '0.9rem' }}
                        >
                          {versions.length > 0 ? (
                            versions.map((v) => {
                              const shortId = v.version_id.slice(-5);
                              const dateStr = v.created_at ? new Date(v.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
                              return (
                                <option key={v.version_id} value={v.version_id}>
                                  Version {shortId} • {dateStr}
                                </option>
                              );
                            })
                          ) : (
                            <option value={planVersion.version_id}>
                              Version {planVersion.version_id.slice(-5)} • {planVersion.days?.[0]?.date ? new Date((planVersion.days[0] as any).date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                            </option>
                          )}
                        </select>
                      </div>
                    </div>
                  )}

                  {/* Sub-tabs: Overview | Nutrition Comparison | Daily Meal Plan | Chat */}
                  <div className="patient-tabs" style={{ marginBottom: 16 }}>
                    <button
                      type="button"
                      className={`patient-tab ${viewTab === 'overview' ? 'active' : ''}`}
                      onClick={() => setViewTab('overview')}
                    >
                      Overview
                    </button>
                    <button
                      type="button"
                      className={`patient-tab ${viewTab === 'nutrient_comparison' ? 'active' : ''}`}
                      onClick={() => setViewTab('nutrient_comparison')}
                    >
                      Nutrition Comparison
                    </button>
                    <button
                      type="button"
                      className={`patient-tab ${viewTab === 'daily_meal_plan' ? 'active' : ''}`}
                      onClick={() => setViewTab('daily_meal_plan')}
                      disabled={!planVersion?.days?.length}
                    >
                      Daily Meal Plan
                    </button>
                    <button
                      type="button"
                      className={`patient-tab ${viewTab === 'chat' ? 'active' : ''}`}
                      onClick={() => setViewTab('chat')}
                    >
                      Chat
                    </button>
                  </div>

                  {/* When version is in_progress and days empty, show CTA to open workflow instead of blank content */}
                  {planVersion && (planVersion.status === 'in_progress' || (planVersion as any).workflow_state === 'in_progress') && (!planVersion.days || planVersion.days.length === 0) && (
                    <div className="np-subcard" style={{ padding: 24, marginBottom: 24, background: '#f0f9ff', border: '1px solid #bae6fd' }}>
                      <h3 style={{ margin: '0 0 8px', fontSize: '1.1rem', color: '#0c4a6e' }}>Plan is still generating</h3>
                      <p style={{ margin: '0 0 16px', fontSize: '0.95rem', color: '#075985' }}>
                        This version has no daily plan data yet. Open the Meal Plan workflow to see progress, complete table review, or approve when ready.
                      </p>
                      <button
                        type="button"
                        className="primary-button"
                        onClick={() => navigate(`/patients/${patientId}/daily-meals-plan/${planId}?editPlan=${planId}&step=meal_plan`, { state: { versionId: planVersion.version_id } })}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                      >
                        Open in Meal Plan workflow
                      </button>
                    </div>
                  )}

              {viewTab === 'overview' && (
                <MealPlanOverviewCards
                  summary={totalsSummary}
                  marginTop={0}
                  emptyMessage="Overview data is not available for this version yet."
                />
              )}

              {viewTab === 'nutrient_comparison' && totalsSummary && (
                <div className="np-subcard" style={{ marginBottom: 16, padding: 16 }}>
                  <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: '1.1rem', fontWeight: 600 }}>
                    Nutrient Comparison: Generated Plan vs Meal Plan
                  </h3>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                          <th style={{ padding: 12, textAlign: 'left', fontWeight: 600 }}>Nutrient</th>
                          <th style={{ padding: 12, textAlign: 'right', fontWeight: 600 }}>Generated Plan</th>
                          <th style={{ padding: 12, textAlign: 'right', fontWeight: 600 }}>Meal Plan Target</th>
                          <th style={{ padding: 12, textAlign: 'right', fontWeight: 600 }}>Difference</th>
                          <th style={{ padding: 12, textAlign: 'center', fontWeight: 600 }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const mealPlanNutrients = new Set(Object.keys(totalsSummary.meal_plan_totals || {}));
                          const allTotals = new Set(Object.keys(totalsSummary.totals || {}));
                          const compared = new Set(Object.keys(totalsSummary.comparison));
                          const combined = Array.from(mealPlanNutrients).concat(Array.from(allTotals), Array.from(compared));
                          const allNutrients = Array.from(new Set(combined)).filter((n) => {
                            if (allTotals.has('calories') && (n === 'energy_kcal' || n === 'calories_kcal')) return false;
                            if (allTotals.has('carbs_g') && n === 'carbohydrates_g') return false;
                            if (allTotals.has('folate_mcg') && n === 'vitamin_b9_folate_mcg') return false;
                            return true;
                          });
                          const sorted = allNutrients.sort((a, b) => {
                            const ai = NUTRIENT_ORDER.indexOf(a);
                            const bi = NUTRIENT_ORDER.indexOf(b);
                            if (ai === -1 && bi === -1) return a.localeCompare(b);
                            if (ai === -1) return 1;
                            if (bi === -1) return -1;
                            return ai - bi;
                          });
                          return sorted.map((nutrient) => {
                            const actual = totalsSummary.totals[nutrient] || 0;
                            let target = totalsSummary.meal_plan_totals?.[nutrient];
                            if (target == null) target = totalsSummary.comparison?.[nutrient]?.target ?? null;
                            if (target == null && nutrient === 'calories') target = totalsSummary.meal_plan_totals?.['calories_kcal'] ?? totalsSummary.meal_plan_totals?.['energy_kcal'] ?? null;
                            if (typeof target === 'string') target = parseFloat(target) || null;
                            const comp = totalsSummary.comparison[nutrient];
                            const diff = target !== null ? actual - target : null;
                            const diffPct = target !== null && target > 0 ? (diff! / target) * 100 : null;
                            const statusColor = !comp?.status ? '#6b7280' : comp.status === 'met' ? '#10b981' : comp.status === 'over' ? '#f59e0b' : '#ef4444';
                            const statusLabel = !comp?.status ? '-' : comp.status === 'met' ? '✓ Met' : comp.status === 'over' ? '↑ Over' : '↓ Under';
                            return (
                              <tr key={nutrient} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                <td style={{ padding: '10px 12px', fontWeight: 500 }}>{nutrient.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}</td>
                                <td style={{ padding: '10px 12px', textAlign: 'right' }}>{actual.toFixed(1)}</td>
                                <td style={{ padding: '10px 12px', textAlign: 'right' }}>{target !== null ? target.toFixed(1) : '-'}</td>
                                <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                                  {diff !== null ? `${diff > 0 ? '+' : ''}${diff.toFixed(1)}${diffPct != null ? ` (${diffPct > 0 ? '+' : ''}${diffPct.toFixed(1)}%)` : ''}` : '-'}
                                </td>
                                <td style={{ padding: '10px 12px', textAlign: 'center', color: statusColor, fontWeight: 600, fontSize: '0.85rem' }}>{statusLabel}</td>
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {viewTab === 'daily_meal_plan' && (!planVersion?.days?.length) && (
                <div className="np-subcard" style={{ padding: 24, color: '#6b7280', textAlign: 'center' }}>
                  {planVersion && (planVersion.status === 'in_progress' || (planVersion as any).workflow_state === 'in_progress') ? (
                    <>
                      <p style={{ margin: '0 0 12px' }}>No daily meal plan data for this version yet. The plan is still generating.</p>
                      <button type="button" className="primary-button" onClick={() => navigate(`/patients/${patientId}/daily-meals-plan/${planId}?editPlan=${planId}&step=meal_plan`, { state: { versionId: planVersion?.version_id } })}>Open in Meal Plan workflow</button>
                    </>
                  ) : (
                    <p style={{ margin: 0 }}>No daily meal plan data for this version yet.</p>
                  )}
                </div>
              )}
              {viewTab === 'daily_meal_plan' && planVersion?.days && planVersion.days.length > 0 && (() => {
                const days = planVersion.days;
                const DAYS_PER_WEEK = 7;
                const start = weekOffset * DAYS_PER_WEEK;
                const weekDays = days.slice(start, start + DAYS_PER_WEEK);
                const selectedIdx = expandedDay != null && expandedDay >= start && expandedDay < start + weekDays.length ? expandedDay : start;
                const selectedDay = weekDays[selectedIdx - start];
                const weekStart = weekDays[0]?.date ? new Date((weekDays[0] as any).date) : null;
                const weekEnd = weekDays[weekDays.length - 1]?.date ? new Date((weekDays[weekDays.length - 1] as any).date) : null;
                const dateRange = weekStart && weekEnd ? `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : '—';
                const totalPages = Math.max(1, Math.ceil(days.length / DAYS_PER_WEEK));
                const maxOffset = Math.max(0, totalPages - 1);
                // Build meal time map: use plan-level meal_timing_json (from DB) when available, else hardcoded defaults
                const planTiming = plan?.meal_timing_json as { meal_times_by_type?: Record<string, string>; meal_slots?: Array<{ meal_type?: string; time?: string }> } | undefined;
                const mealTimeByKey: Record<string, string> = {
                  breakfast: defaultMealTiming.breakfast,
                  lunch: defaultMealTiming.lunch,
                  dinner: defaultMealTiming.dinner,
                  snack: defaultMealTiming.snacks,
                };
                if (planTiming?.meal_times_by_type && typeof planTiming.meal_times_by_type === 'object') {
                  Object.entries(planTiming.meal_times_by_type).forEach(([k, v]) => {
                    if (v && typeof v === 'string') {
                      mealTimeByKey[k] = v;
                      mealTimeByKey[k.toLowerCase().replace(/\s+/g, '_')] = v;
                    }
                  });
                }
                if (Array.isArray(planTiming?.meal_slots)) {
                  planTiming.meal_slots.forEach((slot: any) => {
                    const key = slot?.meal_type ?? slot?.meal_key;
                    const t = slot?.time ?? slot?.meal_time;
                    if (key && t) {
                      const normalized = String(key).toLowerCase().replace(/\s+/g, '_');
                      mealTimeByKey[normalized] = String(t);
                      mealTimeByKey[String(key)] = String(t);
                    }
                  });
                }
                const mealMeta: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
                  breakfast: { label: 'Breakfast', icon: <Coffee size={22} />, color: '#c2410c' },
                  lunch: { label: 'Lunch', icon: <Sun size={22} />, color: '#ca8a04' },
                  dinner: { label: 'Dinner', icon: <Moon size={22} />, color: '#1e40af' },
                  snack: { label: 'Snacks', icon: <Cookie size={22} />, color: '#16a34a' },
                };
                const getMealMeta = (mealKey: string) => {
                  const known = mealMeta[mealKey] || mealMeta[(mealKey || '').toLowerCase().replace(/\s+/g, '_')];
                  if (known) return known;
                  return { label: formatMealKeyForDisplay(mealKey || 'snack'), icon: mealMeta.snack.icon, color: mealMeta.snack.color };
                };
                const mealOrder: Record<string, number> = { breakfast: 0, lunch: 1, dinner: 2, snack: 3 };
                const macroColors = { protein_g: '#8b5cf6', carbs_g: '#7dd3fc', fat_g: '#1e40af', fiber_g: '#9ca3af' };
                const formatTime = (t: string) => {
                  const [h, m] = (t || '00:00').split(':').map(Number);
                  return `${h % 12 || 12}:${String(m || 0).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
                };
                const timeToMinutes = (t: string | null | undefined): number | null => {
                  if (!t || typeof t !== 'string') return null;
                  const parts = t.trim().split(':').map(Number);
                  const h = parts[0] ?? 0, m = parts[1] ?? 0;
                  if (!Number.isFinite(h)) return null;
                  return h * 60 + m;
                };
                const getTimeForItem = (item: any) => item?.meal_time || mealTimeByKey[item?.meal_key] || mealTimeByKey[(item?.meal_key || '').toLowerCase().replace(/\s+/g, '_')];
                return (
                  <div className="np-subcard" style={{ padding: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
                      <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>Weekly Schedule</h3>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={handleViewPdf}
                          disabled={!!pdfLoading}
                          className="secondary-button"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', fontSize: '0.875rem' }}
                        >
                          <FileText size={16} />
                          {pdfLoading === 'view' ? 'Opening…' : 'Generate and View'}
                        </button>
                        <button
                          type="button"
                          onClick={handleSendViaWhatsApp}
                          disabled={whatsappSending || !!pdfLoading}
                          className="primary-button"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', fontSize: '0.875rem' }}
                        >
                          <MessageCircle size={16} />
                          {whatsappSending ? 'Sending…' : 'Send diet plan via WhatsApp'}
                        </button>
                        <button
                          type="button"
                          onClick={handleDownloadPdf}
                          disabled={!!pdfLoading}
                          className="secondary-button"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', fontSize: '0.875rem' }}
                        >
                          <Download size={16} />
                          {pdfLoading === 'download' ? 'Downloading…' : 'Download PDF'}
                        </button>
                        <span style={{ width: 1, height: 24, background: '#e5e7eb', margin: '0 4px' }} aria-hidden />
                        <button type="button" disabled={weekOffset <= 0} onClick={() => setWeekOffset((o) => Math.max(0, o - 1))} style={{ padding: 6, border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', cursor: weekOffset <= 0 ? 'not-allowed' : 'pointer', opacity: weekOffset <= 0 ? 0.5 : 1 }}>
                          <ChevronLeft size={18} />
                        </button>
                        <span style={{ fontSize: '0.9rem', fontWeight: 500, color: '#374151', minWidth: 72, textAlign: 'center' }}>Page {weekOffset + 1} of {totalPages}</span>
                        <button type="button" disabled={weekOffset >= maxOffset} onClick={() => setWeekOffset((o) => Math.min(maxOffset, o + 1))} style={{ padding: 6, border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', cursor: weekOffset >= maxOffset ? 'not-allowed' : 'pointer', opacity: weekOffset >= maxOffset ? 0.5 : 1 }}>
                          <ChevronRight size={18} />
                        </button>
                      </div>
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#111827' }}>Current Week</h4>
                      <span style={{ fontSize: '0.9rem', color: '#6b7280' }}>{dateRange}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', width: '100%', marginBottom: 16 }}>
                      {weekDays.map((day, idx) => {
                        const dayIdx = start + idx;
                        const isSelected = selectedIdx === dayIdx;
                        const d = day.date ? new Date(day.date as string) : new Date();
                        const label = d.toLocaleDateString('en-US', { weekday: 'short' }) + ' ' + d.getDate();
                        return (
                          <button
                            key={dayIdx}
                            type="button"
                            onClick={() => setExpandedDay(dayIdx)}
                            style={{
                              flex: '1 1 0',
                              minWidth: 0,
                              padding: '10px 8px',
                              borderRadius: 8,
                              border: '1px solid #e5e7eb',
                              background: isSelected ? '#1e40af' : '#f9fafb',
                              color: isSelected ? '#fff' : '#374151',
                              cursor: 'pointer',
                              fontSize: '0.9rem',
                              fontWeight: 600,
                              boxShadow: isSelected ? '0 1px 3px rgba(0,0,0,0.15)' : 'none',
                            }}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                    {selectedDay && (
                      <div style={{ padding: '16px 0' }}>
                        {(() => {
                          const dayItems = ((selectedDay as any)?.items || []).slice().sort((a: any, b: any) => {
                            const ma = timeToMinutes(getTimeForItem(a)), mb = timeToMinutes(getTimeForItem(b));
                            if (ma != null && mb != null) return ma - mb;
                            if (ma != null) return -1;
                            if (mb != null) return 1;
                            return (mealOrder[a?.meal_key] ?? 4) - (mealOrder[b?.meal_key] ?? 4);
                          });
                          if (dayItems.length === 0) return <div style={{ padding: 16, color: '#9ca3af', fontSize: '0.9rem' }}>No meals for this day.</div>;
                          const mealKeyNorm = (item: any) => (item?.meal_key ?? 'snack').toString().toLowerCase().replace(/\s+/g, '_');
                          const groupedByMeal = new Map<string, any[]>();
                          for (const item of dayItems) {
                            const k = mealKeyNorm(item);
                            if (!groupedByMeal.has(k)) groupedByMeal.set(k, []);
                            groupedByMeal.get(k)!.push(item);
                          }
                          const mealOrderList = ['breakfast', 'lunch', 'dinner', 'snack'];
                          const orderedGroups = mealOrderList.filter((k) => groupedByMeal.has(k)).concat(Array.from(groupedByMeal.keys()).filter((k) => !mealOrderList.includes(k)));
                          return orderedGroups.map((mealKey) => {
                            const items = groupedByMeal.get(mealKey)!;
                            const first = items[0];
                            const mt = first?.meal_key ?? mealKey;
                            const meta = getMealMeta(mt);
                            const rawTime = first?.meal_time || mealTimeByKey[mealKey] || mealTimeByKey[mt];
                            const timeStr = rawTime ? formatTime(rawTime) : '—';
                            const groupKcal = items.reduce((sum, m) => sum + (m?.dish_nutrients?.values?.calories != null ? Number(m.dish_nutrients.values.calories) : 0), 0);
                            const groupKcalRounded = Math.round(groupKcal);
                            const groupTabKey = `v-${selectedIdx}-${mealKey}`;
                            const activeDetail = detailTab[groupTabKey] ?? 'ingredients';
                            const setActive = (tab: 'ingredients' | 'preparations') => setDetailTab((prev) => ({ ...prev, [groupTabKey]: tab }));
                            const summedMacros = items.reduce((acc, m) => {
                              const v = m?.dish_nutrients?.values || {};
                              acc.protein_g += Number(v.protein_g) || 0;
                              acc.carbs_g += Number(v.carbs_g) || 0;
                              acc.fat_g += Number(v.fat_g) || 0;
                              acc.fiber_g += Number(v.fiber_g) || 0;
                              return acc;
                            }, { protein_g: 0, carbs_g: 0, fat_g: 0, fiber_g: 0 });
                            const macroTotal = summedMacros.protein_g + summedMacros.carbs_g + summedMacros.fat_g + summedMacros.fiber_g;
                            const segments = macroTotal > 0 ? [
                              { key: 'protein_g', value: summedMacros.protein_g, color: macroColors.protein_g, label: 'Protein' },
                              { key: 'carbs_g', value: summedMacros.carbs_g, color: macroColors.carbs_g, label: 'Carbs' },
                              { key: 'fat_g', value: summedMacros.fat_g, color: macroColors.fat_g, label: 'Fats' },
                              { key: 'fiber_g', value: summedMacros.fiber_g, color: macroColors.fiber_g, label: 'Fiber' },
                            ].filter((s) => s.value > 0) : [];
                            const chartSize = 200;
                            return (
                              <div key={mealKey} style={{ marginBottom: 28, border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                                <div style={{ padding: '16px 16px 12px', borderBottom: `2px solid ${meta.color}`, backgroundColor: '#fafafa' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <span style={{ color: meta.color, flexShrink: 0 }}>{meta.icon}</span>
                                    <span style={{ fontSize: '1.1rem', fontWeight: 700, color: '#111827' }}>{meta.label}</span>
                                    <span style={{ fontSize: '0.9rem', color: '#6b7280', fontWeight: 500 }}>• {timeStr}{groupKcalRounded > 0 ? ` • ${groupKcalRounded} kcal total` : ''}</span>
                                  </div>
                                </div>
                                <div style={{ padding: '12px 16px 8px', borderBottom: '1px solid #e5e7eb', display: 'flex', gap: 0 }}>
                                  <button type="button" onClick={() => setActive('ingredients')} style={{ padding: '8px 14px', border: 'none', background: 'none', fontSize: '0.85rem', fontWeight: 500, color: activeDetail === 'ingredients' ? '#2563eb' : '#6b7280', borderBottom: activeDetail === 'ingredients' ? '2px solid #2563eb' : '2px solid transparent', marginBottom: -1, cursor: 'pointer' }}>Ingredients & Nutrients</button>
                                  <button type="button" onClick={() => setActive('preparations')} style={{ padding: '8px 14px', border: 'none', background: 'none', fontSize: '0.85rem', fontWeight: 500, color: activeDetail === 'preparations' ? '#2563eb' : '#6b7280', borderBottom: activeDetail === 'preparations' ? '2px solid #2563eb' : '2px solid transparent', marginBottom: -1, cursor: 'pointer' }}>Preparation</button>
                                </div>
                                <div style={{ padding: 16 }}>
                                  {activeDetail === 'ingredients' && (
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 24, alignItems: 'start' }}>
                                      <div>
                                        <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#6b7280', letterSpacing: '0.05em', marginBottom: 10 }}>INGREDIENTS</div>
                                        <div style={{ fontSize: '0.9rem', lineHeight: 1.8 }}>
                                          {items.flatMap((meal: any) => (meal?.ingredients || []).map((ing: any) => typeof ing === 'string' ? ing : (ing.name || '') + (ing.portion_display ? ` ${ing.portion_display}` : '') + (ing.exception ? ` (${ing.exception})` : ''))).map((line: string, idx: number) => (
                                            <div key={idx}>{line}</div>
                                          ))}
                                          {items.some((m: any) => m?.exception) && items.filter((m: any) => m?.exception).map((meal: any, i: number) => (
                                            <div key={`ex-${i}`} style={{ color: '#b45309', marginTop: 4 }}>{meal.dish_name || 'Dish'}: {meal.exception}</div>
                                          ))}
                                          {items.every((m: any) => !(m?.ingredients?.length)) && !items.some((m: any) => m?.exception) && <div style={{ color: '#9ca3af' }}>No ingredients</div>}
                                        </div>
                                      </div>
                                      <div>
                                        <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#6b7280', letterSpacing: '0.05em', marginBottom: 10 }}>MACRONUTRIENTS DISTRIBUTION</div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
                                          <div style={{ position: 'relative', width: chartSize, height: chartSize, flexShrink: 0 }}>
                                            {segments.length > 0 ? (
                                              <>
                                                <svg width={chartSize} height={chartSize} viewBox="0 0 120 120" style={{ transform: 'rotate(-90deg)' }}>
                                                  {segments.reduce((acc: { offset: number; elements: React.ReactNode[] }, s) => {
                                                    const pct = s.value / macroTotal;
                                                    const start = acc.offset;
                                                    const end = start + pct;
                                                    const x1 = 60 + 48 * Math.cos(start * 2 * Math.PI);
                                                    const y1 = 60 + 48 * Math.sin(start * 2 * Math.PI);
                                                    const x2 = 60 + 48 * Math.cos(end * 2 * Math.PI);
                                                    const y2 = 60 + 48 * Math.sin(end * 2 * Math.PI);
                                                    acc.elements.push(<path key={s.key} d={`M 60 60 L ${x1} ${y1} A 48 48 0 ${pct > 0.5 ? 1 : 0} 1 ${x2} ${y2} Z`} fill={s.color} />);
                                                    acc.offset = end;
                                                    return acc;
                                                  }, { offset: 0, elements: [] }).elements}
                                                  <circle cx={60} cy={60} r={28} fill="#fff" />
                                                </svg>
                                                <div style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '1.1rem' }}>{groupKcalRounded} KCAL</div>
                                              </>
                                            ) : (
                                              <div style={{ width: chartSize, height: chartSize, borderRadius: '50%', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', color: '#9ca3af' }}>No data</div>
                                            )}
                                          </div>
                                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                            {segments.length > 0 ? segments.map((s) => (
                                              <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9rem' }}>
                                                <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: s.color }} />
                                                <span>{s.label}: {Number(summedMacros[s.key as keyof typeof summedMacros])?.toFixed(0) ?? 0}g</span>
                                              </div>
                                            )) : <div style={{ fontSize: '0.85rem', color: '#9ca3af' }}>No macro data</div>}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                  {activeDetail === 'preparations' && (
                                    <div>
                                      <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#6b7280', letterSpacing: '0.05em', marginBottom: 10 }}>STEP-BY-STEP INSTRUCTIONS</div>
                                      {items.map((meal: any, mealIdx: number) => {
                                        const steps = meal?.recipe_steps || meal?.premium_recipe?.steps || [];
                                        return (
                                          <div key={mealIdx} style={{ marginBottom: mealIdx < items.length - 1 ? 16 : 0 }}>
                                            <div style={{ fontWeight: 600, color: '#111827', marginBottom: 6, fontSize: '0.95rem' }}>{meal?.dish_name || 'Dish'}</div>
                                            <ol style={{ margin: 0, paddingLeft: 20, fontSize: '0.9rem', lineHeight: 1.7 }}>
                                              {steps.length > 0 ? steps.map((step: string, i: number) => <li key={i} style={{ marginBottom: 8 }}>{step}</li>) : <li style={{ color: '#9ca3af' }}>No preparation steps</li>}
                                            </ol>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    )}
                    {(() => {
                      const gl = (planVersion as any).grocery_list || [];
                      if (!Array.isArray(gl) || gl.length === 0) return null;
                      return (
                        <div style={{ marginTop: 24 }}>
                          <button type="button" onClick={() => setExpandedGrocery((p) => !p)} style={{ width: '100%', padding: '12px 16px', background: expandedGrocery ? '#f3f4f6' : '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '1rem', fontWeight: 600 }}>
                            <span>🛒 Grocery List ({gl.length} items)</span>
                            <span>{expandedGrocery ? '▼' : '▶'}</span>
                          </button>
                          {expandedGrocery && (
                            <div style={{ marginTop: 8, padding: 16, backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, overflowX: 'auto' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                                <thead>
                                  <tr style={{ borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Ingredient</th>
                                    <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>Amount</th>
                                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Category</th>
                                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Notes</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {gl.map((gi: any, gIdx: number) => {
                                    const name = typeof gi === 'string' ? gi : gi.ingredient_name || gi.name || gi.item || 'Unknown';
                                    const amount = typeof gi === 'object' ? (gi.total_amount || gi.quantity || gi.amount || '') : '';
                                    const unit = typeof gi === 'object' ? (gi.unit || gi.portion_display || '') : '';
                                    const displayAmount = amount && unit ? `${amount} ${unit}` : amount || unit || '-';
                                    const category = typeof gi === 'object' ? (gi.category || gi.food_group || '') : '';
                                    const notes = typeof gi === 'object' ? (gi.notes || gi.source || '') : '';
                                    return (
                                      <tr key={gIdx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                        <td style={{ padding: '8px 12px', fontWeight: 500 }}>{name}</td>
                                        <td style={{ padding: '8px 12px', textAlign: 'right' }}>{displayAmount}</td>
                                        <td style={{ padding: '8px 12px', color: '#6b7280' }}>{category || '-'}</td>
                                        <td style={{ padding: '8px 12px', color: '#6b7280', fontSize: '0.85em' }}>{notes || '-'}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                );
              })()}

              {viewTab === 'chat' && (
                <div className="np-subcard" style={{ padding: 16 }}>
                  {loadingChatStatus && !chatStatus && <div style={{ padding: 16, textAlign: 'center', color: '#6b7280' }}>Loading chat...</div>}
                  {!hasSession && !loadingChatStatus && (
                    <div style={{ padding: 16, backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 16, textAlign: 'center' }}>
                      <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748b' }}>Use the chat below to refine requirements and generate a new draft.</p>
                    </div>
                  )}
                  <div ref={chatMessagesContainerRef} style={{ maxHeight: 400, overflowY: 'auto', marginBottom: 16 }}>
                    {chatStatus?.conversation_log && chatStatus.conversation_log.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {chatStatus.conversation_log.map((msg: any, idx: number) => {
                          const isDoctor = msg.role === 'user' || msg.role === 'doctor';
                          const questionText = msg.content || '';
                          const options = (chatStatus?.current_ambiguity_question && chatStatus.waiting_for_doctor_response) ? (chatStatus.current_ambiguity_question.options || chatStatus.current_ambiguity_question.suggestions || []) : [];
                          const messageKey = msg.created_at ? `${msg.created_at}-${idx}` : `msg-${idx}`;
                          return (
                            <div key={idx} style={{ alignSelf: isDoctor ? 'flex-end' : 'flex-start', maxWidth: '70%' }}>
                              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexDirection: isDoctor ? 'row-reverse' : 'row' }}>
                                {!isDoctor && <span style={{ color: '#94a3b8', flexShrink: 0 }}><Sparkles size={16} /></span>}
                                <div>
                                  <div style={{ padding: '12px 16px', borderRadius: 12, backgroundColor: isDoctor ? '#2563eb' : '#f1f5f9', color: isDoctor ? '#fff' : '#0f172a', fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {questionText}
                                  </div>
                                  <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 6, textAlign: isDoctor ? 'right' : 'left' }}>
                                    {isDoctor ? 'YOU' : 'AI ASSISTANT'} {msg.created_at ? ` · ${new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}
                                  </div>
                                </div>
                              </div>
                              {!isDoctor && options.length > 0 && (
                                <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                  {options.map((opt: string, oi: number) => (
                                    <button
                                      key={oi}
                                      type="button"
                                      onClick={() => sendChatMessage(opt, sessionId)}
                                      disabled={sendingChat}
                                      style={{ padding: '6px 12px', backgroundColor: '#fff', color: '#374151', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, cursor: sendingChat ? 'not-allowed' : 'pointer' }}
                                    >
                                      {opt}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{ color: '#9ca3af', fontSize: 14, textAlign: 'center', padding: 20 }}>
                        {chatStatus?.waiting_for_doctor_response ? 'Loading question...' : chatStatus?.status === 'processing' ? 'Processing...' : 'No messages yet. Type below to start.'}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input
                      type="text"
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey && chatInput.trim() && !sendingChat) {
                          e.preventDefault();
                          sendChatMessage(chatInput.trim(), sessionId ?? undefined);
                        }
                      }}
                      placeholder="Type a message or e.g. Create new plan..."
                      style={{ flex: 1, padding: '12px 16px', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 14, backgroundColor: '#f8fafc' }}
                      disabled={sendingChat}
                    />
                    <button
                      type="button"
                      onClick={() => chatInput.trim() && sendChatMessage(chatInput.trim(), sessionId ?? undefined)}
                      disabled={sendingChat || !chatInput.trim()}
                      style={{ width: 44, height: 44, borderRadius: '50%', border: 'none', background: '#2563eb', color: '#fff', cursor: sendingChat || !chatInput.trim() ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: sendingChat || !chatInput.trim() ? 0.7 : 1 }}
                    >
                      <Send size={18} />
                    </button>
                  </div>
                </div>
              )}
                </>
              )}
            </div>
          </main>
        </div>
      </div>
      <PdfViewerModal
        open={!!pdfViewerUrl}
        pdfUrl={pdfViewerUrl}
        title="Meal Plan"
        onClose={handleClosePdfViewer}
      />
      <AlertModal
        open={!!whatsappMessage}
        title="Send diet plan via WhatsApp"
        message={whatsappMessage?.text ?? ''}
        variant={whatsappMessage?.type === 'error' ? 'error' : 'success'}
        onClose={() => setWhatsappMessage(null)}
      />
    </div>
  );
};

export default MealPlanVersionViewPage;
